import React, { ReactNode, RefObject, useCallback, useEffect, useState } from 'react'
import { Image, Keyboard, Platform, StyleSheet, TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

import { BottomSheetBackdrop, BottomSheetModal } from '@gorhom/bottom-sheet'
import { useRouter } from 'expo-router'

import DividerGradient from '../../../assets/divider-gradient.svg'
import HamburgerMenu from '../../../assets/hamburger-menu.svg'
import { DS } from '../../../design-system'
import { EnvironmentStages, useStore } from '../../../models'
import { observer } from 'mobx-react-lite'
import { firstLetterToUpper } from '@ppwr/poker'
import { ReduceMotion } from 'react-native-reanimated'

type CheatSheetProps = {
  stage: EnvironmentStages | null
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
}

export const WelcomeModal = observer(({ bottomSheetModalRef, stage }: CheatSheetProps) => {
  const router = useRouter()
  const [enableDismissOnClose, setEnableDismissOnClose] = useState(true)
  const {
    memberships: { subscription }
  } = useStore()

  const CustomLinkProps = [
    //NOTE: Hide Community entry point - https://peak6.atlassian.net/browse/PP-3709
    // {
    //   id: 1,
    //   icon: 'Globe',
    //   onPress: () => {
    //     bottomSheetModalRef.current?.close()
    //     navigation.navigate(DrawerRoutes.community, {
    //       url: stage === EnvironmentStages.prod ? COMMUNITY_URL : COMMUNITY_URL_DEV
    //     })
    //   },
    //   feature: (
    //     <DS.Text variant="Body Medium/Regular">
    //       Network and mingle with other members in our <DS.Text variant="Body Medium/Medium">Community</DS.Text>
    //     </DS.Text>
    //   )
    // },
    {
      id: 2,
      icon: 'Book',
      onPress: () => {
        bottomSheetModalRef.current?.close()
        router.navigate('/(root)/(tabs)/learning')
      },
      feature: (
        <DS.Text variant="Body Medium/Regular">
          Start learning now with our catalog of on-demand classes on the{' '}
          <DS.Text variant="Body Medium/Medium">Learn</DS.Text> tab
        </DS.Text>
      )
    }
  ]

  const CustomButtonLink = ({ icon, onPress, feature }: { icon: string; onPress: () => void; feature: ReactNode }) => {
    return (
      <DS.Frame style={styles.buttonLinkContainer}>
        <TouchableOpacity style={styles.buttonLink} onPress={onPress}>
          <DS.Frame style={styles.buttonLinkIconContainer}>
            <DS.Frame style={styles.buttonLinkIcon}>
              <DS.Icon name={icon as never} size={24} color="Global/White" />
            </DS.Frame>
            <DS.Frame style={{ flex: 1 }}>{feature}</DS.Frame>
          </DS.Frame>
          <DS.Icon style={styles.leftArrowIcon} name="Arrow--right-wide" size={10} color="Global/Black" />
        </TouchableOpacity>
      </DS.Frame>
    )
  }

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  )

  useEffect(() => {
    if (Platform.OS !== 'android') return

    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => setEnableDismissOnClose(false))
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => setEnableDismissOnClose(true))

    return () => {
      keyboardDidShowListener.remove()
      keyboardDidHideListener.remove()
    }
  }, [])

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      index={0}
      snapPoints={['70%']}
      backdropComponent={renderBackdrop}
      detached={false}
      handleStyle={styles.bottomSheetHandleStyle}
      handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
      keyboardBlurBehavior="restore"
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      enableDismissOnClose={enableDismissOnClose}
      overrideReduceMotion={ReduceMotion.System}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        <DS.Frame style={styles.container}>
          <DS.Icon
            style={styles.closeIcon}
            name="Close-bg"
            size={32}
            color="Brand/Light Grey"
            onPress={() => bottomSheetModalRef.current?.close()}
          />
          <DS.Frame style={styles.header}>
            <DS.Text variant="Body Medium/Regular">Thank you for joining</DS.Text>
            <DS.Text variant="Heading 1/Medium">{`Premium ${
              subscription?.type ? firstLetterToUpper(subscription?.type) : ''
            }!`}</DS.Text>
          </DS.Frame>

          <DS.Frame style={styles.imageContainer}>
            <Image style={styles.heroImage} source={require('../../../assets/welcome_hero.png')} />
          </DS.Frame>

          <DS.Frame style={styles.textContainer}>
            <DS.Text variant="Heading 5/Medium">What would you like to do first?</DS.Text>
            <DividerGradient width={40} />
          </DS.Frame>

          <DS.Frame style={styles.description}>
            {CustomLinkProps.map((item) => (
              <CustomButtonLink key={item.id} feature={item.feature} icon={item.icon} onPress={item.onPress} />
            ))}
          </DS.Frame>

          <TouchableOpacity
            style={styles.buttonGoToManage}
            onPress={() => {
              bottomSheetModalRef.current?.close()
              router.navigate('/lobby/membership')
            }}
          >
            <DS.Text variant="Body Small/Regular">Manage your membership in menu</DS.Text>
            <HamburgerMenu />
          </TouchableOpacity>
        </DS.Frame>
      </ScrollView>
    </BottomSheetModal>
  )
})

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: DS.palette['Brand/Cream']
  },

  header: {
    marginBottom: 16
  },
  description: {
    fontSize: 16,
    marginBottom: 24
  },
  buttonLinkContainer: {
    marginTop: 4
  },
  buttonLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: DS.palette['Global/White'],
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 16,
    borderRadius: 48
  },
  buttonLinkIconContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    paddingRight: 16,
    marginRight: 16
  },
  buttonLinkIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/Black'],
    width: 48,
    height: 48,
    boxShadow: '0px 4px 8px 0px #0000001A',
    borderRadius: 48
  },
  bottomSheetHandleStyle: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 0,
    height: 16
  },
  bottomSheetIndicatorStyle: {
    backgroundColor: 'gray',
    height: 2,
    width: 44
  },
  buttonGoToManage: {
    flexDirection: 'row',
    gap: 8,
    backgroundColor: DS.palette['Brand/Cream'],
    justifyContent: 'center',
    alignItems: 'center'
  },
  imageContainer: {
    marginBottom: 16
  },
  heroImage: {
    width: '100%',
    height: 171,
    borderRadius: 8
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16
  },
  leftArrowIcon: {
    marginLeft: -12
  },
  closeIcon: {
    position: 'absolute',
    top: 8,
    right: 16
  }
})
